/**
 * PublicEventsPage ✦ iter217 — /events
 *
 * Public showcase of weddings/proms/parties EventDrop has captured.
 * Distinct from the host's personal /event/:slug surface — this is
 * the SEO-facing "recent work" page.
 */
import React, { useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import ServicesNav from '../components/services/ServicesNav';
import BrandedPlaceholder from '../components/services/BrandedPlaceholder';
import EnquiryForm from '../components/services/EnquiryForm';
import {
  SERIF, IVORY, IVORY_DIM, IVORY_GHOST,
  GOLD, GOLD_MUTED, GOLD_FAINT, GOLD_HAIRLINE,
  WALNUT_CARD, PAGE_BG, PILL_BUTTON_PRIMARY,
} from '../components/services/_tokens';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const EVENT_TYPE_LABELS = {
  wedding: 'Weddings', corporate: 'Corporate', birthday: 'Birthdays',
  anniversary: 'Anniversaries', christmas_party: 'Christmas Parties',
  school_prom: 'School Proms', charity: 'Charity', baby_shower: 'Baby Showers',
  engagement: 'Engagements', prom: 'Proms', other: 'Other',
};
const PACKAGE_LABELS = {
  'blossom-booth': 'Blossom Booth',
  'heritage-booth': 'Heritage Booth',
  'heritage-mirror-booth': 'Heritage Mirror Booth',
  'woodland-mirror-booth': 'Woodland Mirror Booth',
  'audio-guestbook': 'Audio Guestbook',
  classic: 'Classic', premium: 'Premium', ultimate: 'Ultimate',
  'print-booth': 'Print Booth',
};
const humanise = (slug, table = {}) => slug && (table[slug] || slug.replace(/-/g, ' ').replace(/\b\w/g, l => l.toUpperCase()));

const PublicEventsPage = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [events, setEvents] = useState([]);
  const [facets, setFacets] = useState({ years: [], counties: [], packages: [], event_types: [] });
  const [enquiryOpen, setEnquiryOpen] = useState(false);
  const [q, setQ] = useState(searchParams.get('q') || '');
  // iter237 · Mobile-safety fix. Rendering 50+ event cards (each
  // with a full-res hero image) in one paint was crashing Chrome on
  // Android during scroll. We now hydrate a batch at a time — the
  // rest of the list is inert until the visitor asks for more.
  const PAGE_SIZE = 24;
  const [visibleCount, setVisibleCount] = useState(PAGE_SIZE);

  const filters = useMemo(() => ({
    year: searchParams.get('year') || '',
    county: searchParams.get('county') || '',
    package: searchParams.get('package') || '',
    event_type: searchParams.get('event_type') || '',
    venue: searchParams.get('venue') || '',
    q: searchParams.get('q') || '',
  }), [searchParams]);

  useEffect(() => {
    // iter232 — SEO meta for the index. Title reflects active filter.
    const filterBits = [];
    if (filters.event_type) filterBits.push(EVENT_TYPE_LABELS[filters.event_type] || filters.event_type);
    if (filters.venue) filterBits.push(`at ${humanise(filters.venue)}`);
    if (filters.year) filterBits.push(filters.year);
    if (filters.county) filterBits.push(`in ${filters.county}`);
    document.title = filterBits.length
      ? `${filterBits.join(' ')} ✦ EventDrop Portfolio`
      : 'Recent Events ✦ EventDrop Portfolio';

    let cancelled = false;
    (async () => {
      try {
        const params = new URLSearchParams();
        for (const [k, v] of Object.entries(filters)) if (v) params.set(k, v);
        const r = await axios.get(`${API}/public-events?${params}`);
        if (!cancelled) {
          setEvents(r.data.events || []);
          setFacets(r.data.facets || facets);
          // iter237 — reset page window whenever a filter changes.
          setVisibleCount(PAGE_SIZE);
        }
      } catch { /* render empty state */ }
    })();
    return () => { cancelled = true; };
  }, [filters.year, filters.county, filters.package, filters.event_type, filters.venue, filters.q]);

  const setFilter = (k, v) => {
    const next = new URLSearchParams(searchParams);
    if (v) next.set(k, v); else next.delete(k);
    setSearchParams(next, { replace: true });
  };
  const clearAll = () => setSearchParams({}, { replace: true });
  const activeCount = Object.values(filters).filter(Boolean).length;

  return (
    <div data-testid="public-events-page" style={{
      minHeight: '100vh', background: PAGE_BG, color: IVORY, fontFamily: SERIF,
    }}>
      <ServicesNav active="services" />
      <section style={{
        padding: 'clamp(72px, 12vw, 130px) clamp(20px, 5vw, 60px) clamp(40px, 6vw, 60px)',
        textAlign: 'center',
      }}>
        <div style={{
          fontFamily: SERIF, fontStyle: 'italic',
          fontSize: 11, letterSpacing: '0.32em', textTransform: 'uppercase',
          color: GOLD_MUTED, marginBottom: 20,
        }}>✦ Recent Events</div>
        <h1 style={{
          fontFamily: SERIF, fontWeight: 500,
          fontSize: 'clamp(34px, 6vw, 56px)', lineHeight: 1.08,
          color: IVORY, margin: 0, letterSpacing: '-0.02em',
        }} data-testid="events-headline">Recent events</h1>
        <p style={{
          fontFamily: SERIF, fontStyle: 'italic',
          fontSize: 'clamp(14px, 1.4vw, 17px)', color: IVORY_DIM,
          maxWidth: 660, margin: '22px auto 0', lineHeight: 1.6,
        }}>
          A collection of weddings, parties, proms and celebrations captured using EventDrop.
        </p>
      </section>

      {/* iter232 — Filter bar */}
      <section style={{ padding: '0 clamp(20px, 5vw, 60px) 20px' }}>
        <div style={{
          maxWidth: 1180, margin: '0 auto',
          background: WALNUT_CARD, border: `1px solid ${GOLD_HAIRLINE}`,
          borderRadius: 14, padding: '18px 22px',
          display: 'grid', gap: 12,
          gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))',
        }} data-testid="events-filters">
          <FilterSelect label="Event type" value={filters.event_type}
            options={facets.event_types} labelMap={EVENT_TYPE_LABELS}
            onChange={(v) => setFilter('event_type', v)}
            testid="filter-event-type" />
          <FilterSelect label="Year" value={filters.year}
            options={facets.years}
            onChange={(v) => setFilter('year', v)}
            testid="filter-year" />
          <FilterSelect label="Package" value={filters.package}
            options={facets.packages} labelMap={PACKAGE_LABELS}
            onChange={(v) => setFilter('package', v)}
            testid="filter-package" />
          <FilterSelect label="County" value={filters.county}
            options={facets.counties}
            onChange={(v) => setFilter('county', v)}
            testid="filter-county" />
          <div>
            <div style={filterLabel}>Search</div>
            <input
              type="search"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') setFilter('q', q); }}
              onBlur={() => { if ((q || '') !== filters.q) setFilter('q', q); }}
              placeholder="Venue or customer…"
              data-testid="filter-q"
              style={filterInput}
            />
          </div>
        </div>
        <div style={{
          maxWidth: 1180, margin: '10px auto 0',
          display: 'flex', justifyContent: 'space-between',
          alignItems: 'center', gap: 12, flexWrap: 'wrap',
        }}>
          <div style={{ fontFamily: SERIF, fontStyle: 'italic', fontSize: 12.5, color: IVORY_DIM }}>
            <span data-testid="events-count">{events.length}</span> event{events.length === 1 ? '' : 's'}
            {activeCount > 0 && (
              <span> · {activeCount} filter{activeCount === 1 ? '' : 's'} active</span>
            )}
          </div>
          {activeCount > 0 && (
            <button type="button" onClick={() => { setQ(''); clearAll(); }}
              data-testid="filter-clear"
              style={{
                background: 'transparent', color: GOLD, border: `1px solid ${GOLD_FAINT}`,
                fontFamily: SERIF, fontSize: 12, letterSpacing: '0.06em',
                padding: '5px 12px', borderRadius: 999, cursor: 'pointer',
              }}>Clear filters ✕</button>
          )}
        </div>
      </section>

      <section style={{ padding: '20px clamp(20px, 5vw, 60px) clamp(80px, 10vw, 120px)' }}>
        <div style={{
          maxWidth: 1180, margin: '0 auto',
          display: 'grid', gap: 22,
          gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
        }}>
          {events.length === 0 && (
            <div data-testid="events-empty" style={{
              gridColumn: '1 / -1', textAlign: 'center',
              padding: 'clamp(40px, 6vw, 70px) 20px',
              fontFamily: SERIF, fontStyle: 'italic', color: IVORY_DIM,
            }}>
              No events match those filters yet. <button type="button" onClick={() => { setQ(''); clearAll(); }}
                style={{ color: GOLD, background: 'none', border: 'none', cursor: 'pointer',
                         fontFamily: SERIF, fontStyle: 'italic', textDecoration: 'underline' }}>
                Show all events
              </button>.
            </div>
          )}
          {events.slice(0, visibleCount).map(ev => {
            const hero = ev.hero_image_url || ev.image_url || ev.cover_image;
            const heroUrl = hero && hero.startsWith('/api/')
              ? `${process.env.REACT_APP_BACKEND_URL}${hero}` : hero;
            const title = ev.title || ev.name;
            const date = ev.event_date || ev.date;
            return (
              <Link
                key={ev.slug}
                to={`/events/${ev.slug}`}
                data-testid={`event-card-${ev.slug}`}
                style={{
                  background: WALNUT_CARD, border: `1px solid ${GOLD_HAIRLINE}`,
                  borderRadius: 12, overflow: 'hidden', textDecoration: 'none',
                  color: IVORY,
                  transition: 'transform 320ms ease, border-color 320ms ease',
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = 'translateY(-4px)';
                  e.currentTarget.style.borderColor = GOLD;
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.borderColor = GOLD_HAIRLINE;
                }}
              >
                <BrandedPlaceholder label={title} sublabel={ev.venue_name} aspectRatio="16 / 10" imageUrl={heroUrl} />
                <div style={{ padding: '22px 24px 24px' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8 }}>
                    <span style={{
                      fontFamily: SERIF, fontStyle: 'italic',
                      fontSize: 10.5, letterSpacing: '0.22em', textTransform: 'uppercase',
                      color: GOLD,
                    }}>
                      {EVENT_TYPE_LABELS[ev.event_type] || humanise(ev.event_type) || 'Event'}
                    </span>
                    {ev.review_rating > 0 && (
                      <span style={{ color: GOLD, fontSize: 12 }}>
                        {'★'.repeat(Math.round(ev.review_rating))}
                      </span>
                    )}
                  </div>
                  <h3 style={{
                    fontFamily: SERIF, fontWeight: 500, fontSize: 20,
                    margin: '8px 0 4px', letterSpacing: '-0.005em',
                  }}>{title}</h3>
                  <div style={{
                    fontFamily: SERIF, fontStyle: 'italic',
                    fontSize: 13, color: IVORY_DIM, marginBottom: 8,
                  }}>{ev.venue_name}{date ? ` · ${date.slice(0, 10)}` : ''}</div>
                  {ev.package_slug && (
                    <div style={{
                      fontFamily: SERIF, fontSize: 12, color: GOLD_MUTED,
                      letterSpacing: '0.02em', marginBottom: 6,
                    }}>{humanise(ev.package_slug, PACKAGE_LABELS)}</div>
                  )}
                  <p style={{
                    fontFamily: SERIF, fontStyle: 'italic',
                    fontSize: 13, color: IVORY_GHOST, margin: 0, lineHeight: 1.5,
                  }}>{(ev.review_snippet || ev.cover_blurb || '').slice(0, 140)}</p>
                </div>
              </Link>
            );
          })}
        </div>
        {events.length > visibleCount && (
          <div style={{
            display: 'flex', flexDirection: 'column', alignItems: 'center',
            gap: 10, marginTop: 36,
          }}>
            <button
              type="button"
              data-testid="events-load-more"
              onClick={() => setVisibleCount(c => c + PAGE_SIZE)}
              style={{
                background: 'transparent',
                border: `1px solid ${GOLD_HAIRLINE}`,
                color: GOLD,
                padding: '13px 30px',
                borderRadius: 999,
                fontFamily: SERIF,
                fontSize: 13,
                letterSpacing: '0.18em',
                textTransform: 'uppercase',
                cursor: 'pointer',
                transition: 'background 200ms ease, border-color 200ms ease',
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = 'rgba(212,170,82,0.10)';
                e.currentTarget.style.borderColor = GOLD;
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = 'transparent';
                e.currentTarget.style.borderColor = GOLD_HAIRLINE;
              }}
            >
              Load more events ✦
            </button>
            <span style={{
              fontFamily: SERIF, fontStyle: 'italic',
              fontSize: 12, color: IVORY_DIM,
            }}>
              Showing {visibleCount} of {events.length}
            </span>
          </div>
        )}
      </section>

      <section style={{
        padding: 'clamp(56px, 8vw, 90px) clamp(20px, 5vw, 60px)',
        textAlign: 'center', borderTop: `1px solid ${GOLD_HAIRLINE}`,
      }}>
        <h2 style={{
          fontFamily: SERIF, fontWeight: 500,
          fontSize: 'clamp(22px, 3.6vw, 32px)', margin: 0, color: IVORY,
          letterSpacing: '-0.01em',
        }}>Your night, captured the same way</h2>
        <p style={{
          fontFamily: SERIF, fontStyle: 'italic',
          color: IVORY_DIM, fontSize: 'clamp(14px, 1.4vw, 16px)',
          margin: '14px auto 26px', maxWidth: 480, lineHeight: 1.55,
        }}>
          Tell us about your evening and we&rsquo;ll confirm availability.
        </p>
        <button
          type="button"
          onClick={() => setEnquiryOpen(true)}
          data-testid="events-cta-enquire"
          style={PILL_BUTTON_PRIMARY}
        >Request a Quote ✦</button>
      </section>

      {enquiryOpen && (
        <EnquiryForm onClose={() => setEnquiryOpen(false)} sourcePage="public-events" />
      )}
    </div>
  );
};

const filterLabel = {
  fontFamily: SERIF, fontStyle: 'italic', fontSize: 11,
  letterSpacing: '0.22em', textTransform: 'uppercase',
  color: GOLD_MUTED, marginBottom: 6,
};
const filterInput = {
  width: '100%', padding: '8px 11px', borderRadius: 8,
  background: 'rgba(0,0,0,0.36)', border: `1px solid ${GOLD_HAIRLINE}`,
  color: IVORY, fontFamily: SERIF, fontSize: 13.5,
};

const FilterSelect = ({ label, value, options = [], labelMap = {}, onChange, testid }) => (
  <div>
    <div style={filterLabel}>{label}</div>
    <select value={value} onChange={(e) => onChange(e.target.value)}
      data-testid={testid} style={filterInput}>
      <option value="">All</option>
      {options.map(o => (
        <option key={o} value={o}>{labelMap[o] || o}</option>
      ))}
    </select>
  </div>
);

export default PublicEventsPage;
